import React, { useRef, useState, useEffect } from 'react';
import { AgCharts } from 'ag-charts-react';
import { Col, Row } from 'react-bootstrap';

interface ChartDataItem {
    category: string;
    value: number;
    fill: string;
}

interface DonutChartProps {
    data: ChartDataItem[];
    title?: string;
    centerText?: {
        label: string;
        value: string | number;
    };
    height?: number;
    width?: number;
    innerRadiusRatio?: number;
    outerRadiusRatio?: number;
    showLegend?: boolean;
    showCustomLegend?: boolean;
    showLabels?: boolean;
    showCalloutLabels?: boolean;
    showTooltip?: boolean;
    colors?: string[];
    className?: string;
    style?: React.CSSProperties;
    onSegmentClick?: (data: ChartDataItem) => void;
}

export default function DonutChart({
    data,
    title = "",
    centerText,
    height = 400,
    width = 400,
    innerRadiusRatio = 0.6,
    outerRadiusRatio = 0.9,
    showLegend = false,
    showCustomLegend = false,
    showLabels = false,
    showCalloutLabels = false,
    showTooltip = true,
    colors,
    className = "",
    style = {},
    onSegmentClick
}: DonutChartProps) {
    
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(width || 400);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const w = entry.contentRect.width;
                if (w > 0) setContainerWidth(w);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const filteredData = data.filter(item => item.value > 0);

    const chartData = filteredData.map((item, index) => ({
        ...item,
        fill: colors ? colors[index] || item.fill : item.fill
    }));

    const chartOptions: any = {
        data: chartData,
        title: {
            text: title,
        },
        height,
        width: containerWidth,
        series: [
            {
                type: "donut",
                calloutLabelKey: "category",
                angleKey: "value",
                innerRadiusRatio: centerText ? Math.max(innerRadiusRatio, 0.65) : innerRadiusRatio,
                outerRadiusRatio,
                fills: chartData.map((item) => item.fill),
                innerLabels: centerText ? [
                    {
                        text: String(typeof centerText.value === 'number' ? centerText.value.toLocaleString() : centerText.value),
                        fontSize: 22,
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        spacing: 2,
                    },
                    {
                        text: centerText.label,
                        fontSize: 10,
                        color: '#6b7280',
                        fontWeight: '500',
                    }
                ] : [],
                calloutLabel: {
                    enabled: showCalloutLabels
                },
                sectorLabel: {
                    enabled: showLabels
                },
                tooltip: showTooltip ? {
                    renderer: (params: any) => {
                        return {
                            content: `${params.datum.category}: ${params.datum.value.toLocaleString()}`
                        };
                    }
                } : { enabled: false },
                listeners: onSegmentClick ? {
                    nodeClick: (event: any) => {
                        if (event.datum) {
                            onSegmentClick(event.datum);
                        }
                    }
                } : undefined
            },
        ],
        legend: {
            enabled: showLegend,
            position: "bottom",
            spacing: 40,
            item: {
                marker: {
                    shape: "circle",
                    size: 8
                },
                label: {
                    fontSize: 14,
                    fontWeight: "normal",
                    color: "#666"
                },
                paddingX: 16,
                paddingY: 8
            }
        },
        background: {
            fill: "transparent"
        },
        padding: {
            top: 10,
            right: 10,
            bottom: 10,
            left: 10
        }
    };

    return (
        <div className={className} style={style} ref={containerRef}>
            <Row className="justify-content-center">
                <Col xs={12}>
                    <div className="d-flex justify-content-center">
                        <div style={{ width: '100%' }}>
                            <AgCharts options={chartOptions} />
                        </div>
                    </div>
                    
                    {showCustomLegend && (
                        <div className="mt-4">
                            <Row className="justify-content-center">
                                <Col md={8}>
                                    <div className="d-flex justify-content-around text-center">
                                        {chartData.map((item, index) => (
                                            <div key={index} className="d-flex flex-column align-items-center">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div 
                                                        className="me-2" 
                                                        style={{ 
                                                            width: '16px', 
                                                            height: '16px', 
                                                            backgroundColor: item.fill, 
                                                            borderRadius: '50%' 
                                                        }}
                                                    ></div>
                                                    <span className="text-muted fw-medium" style={{ fontSize: '16px' }}>
                                                        {item.category}
                                                    </span>
                                                </div>
                                                <div className="fw-bold" style={{ fontSize: '24px', color: item.fill }}>
                                                    {item.value.toLocaleString()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}

// Export types for use in other components
export type { ChartDataItem, DonutChartProps };
